import React, { useState, useEffect } from 'react';
import { Spinner } from './Spinner';
// FIX: Replace missing service imports with apiService
import { apiService } from '../services/apiService';
import { ProductSuggestionModal } from './ProductSuggestionModal';
import { ProductMapping, ProductGroup } from '../types';
import { DeleteIcon } from './icons/DeleteIcon';
import { ConfirmModal } from './ConfirmModal';


export const ProductNormalizer: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<ProductGroup[] | null>(null);
    const [analysisResult, setAnalysisResult] = useState<string | null>(null);
    const [mappings, setMappings] = useState<ProductMapping[]>([]);
    const [mappingToDelete, setMappingToDelete] = useState<ProductMapping | null>(null);

    const fetchMappings = async () => {
        setIsLoading(true);
        setLoadingMessage("Načítám existující pravidla...");
        try {
            // FIX: Use apiService
            const existingMappings = await apiService.getAll<ProductMapping>('productMappings');
            setMappings(existingMappings);
        } catch(e: any) {
            setError(e.message || "Nepodařilo se načíst pravidla.");
        } finally {
            setIsLoading(false);
            setLoadingMessage("");
        }
    };
    
    useEffect(() => {
        fetchMappings();
    }, []);

    const handleAnalyze = async () => {
        setIsLoading(true);
        setError(null);
        setAnalysisResult(null);
        setSuggestions(null);

        try {
            setLoadingMessage('Načítám všechny unikátní názvy produktů...');
            // FIX: Use apiService
            const allNames = await apiService.getAllUniqueProductNames();
            
            const mappedNames = new Set<string>();
            mappings.forEach(m => {
                mappedNames.add(m.originalName);
                mappedNames.add(m.canonicalName);
            });

            const namesToAnalyze = allNames.filter(name => !mappedNames.has(name));

            if (namesToAnalyze.length < 2) {
                setAnalysisResult("Nebyly nalezeny žádné nové produkty k analýze a sloučení.");
                setIsLoading(false);
                return;
            }

            setLoadingMessage(`AI analyzuje ${namesToAnalyze.length} nových názvů produktů...`);
            // FIX: Use apiService
            const result = await apiService.groupSimilarProducts(namesToAnalyze);
            
            if (result.length === 0) {
                 setAnalysisResult("AI nenalezla v nových produktech žádné skupiny, které by bylo možné sloučit.");
            } else {
                setSuggestions(result);
            }

        } catch (e: any) {
            setError(e.message || "Došlo k neznámé chybě během analýzy.");
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    const handleConfirmSuggestions = async () => {
        if (!suggestions) return;

        setIsLoading(true);
        setError(null);
        setLoadingMessage('Aktualizuji názvy produktů a ukládám nová pravidla. Může to chvíli trvat...');
        const suggestionsToProcess = suggestions;
        setSuggestions(null);

        try {
            // FIX: Use apiService
            // Update receipts with new normalization rules
            const { updatedCount } = await apiService.normalizeProductNames(suggestionsToProcess);
            // Save the new rules to the database
            await apiService.saveProductMappings(suggestionsToProcess);
            // Refresh the list of rules from the database
            await fetchMappings();

            setAnalysisResult(`Proces dokončen. Bylo aktualizováno ${updatedCount} záznamů v účtenkách a uloženo ${suggestionsToProcess.length} nových pravidel.`);
        } catch (e: any) {
             setError(e.message || "Došlo k chybě při aktualizaci databáze.");
        } finally {
            setIsLoading(false);
            setLoadingMessage('');
        }
    };
    
    const handleConfirmDeleteMapping = async () => {
        if (!mappingToDelete) return;

        try {
            // FIX: Use apiService
            await apiService.deleteProductMapping(mappingToDelete.originalName);
            setMappings(prev => prev.filter(m => m.originalName !== mappingToDelete!.originalName));
            setMappingToDelete(null);
        } catch (e: any) {
             setError(e.message || "Nepodařilo se smazat pravidlo.");
        }
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow space-y-4">
                <h3 className="text-xl font-semibold text-gray-700">Slučování názvů produktů</h3>
                <p className="text-sm text-gray-600">
                    Tento nástroj využívá AI k analýze nových, dosud nezpracovaných položek na vašich účtenkách. Najde produkty, které se jmenují podobně (např. "Mléko polot." a "Polotučné mléko"), a navrhne jejich sjednocení pod jeden název. To výrazně zpřesní sledování cen.
                </p>
                
                <div className="pt-2">
                    <button
                        onClick={handleAnalyze}
                        disabled={isLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                    >
                        Analyzovat nové produkty
                    </button>
                </div>

                {isLoading && !suggestions && <Spinner text={loadingMessage} />}
                
                {error && (
                    <div className="p-3 bg-red-100 text-red-700 text-sm rounded-md border border-red-200">
                        <strong>Chyba:</strong> {error}
                    </div>
                )}

                {analysisResult && (
                     <div className="p-3 bg-green-100 text-green-700 text-sm rounded-md border border-green-200">
                        {analysisResult}
                    </div>
                )}
            </div>
            
             <div className="bg-white p-6 rounded-lg shadow space-y-4">
                 <h3 className="text-xl font-semibold text-gray-700">Existující pravidla ({mappings.length})</h3>
                 <div className="max-h-96 overflow-y-auto border rounded-md">
                     <table className="min-w-full text-sm">
                         <thead className="bg-gray-50 sticky top-0">
                             <tr>
                                 <th className="py-2 px-4 text-left font-medium text-gray-600">Původní název</th>
                                 <th className="py-2 px-4 text-left font-medium text-gray-600">Sjednocený název</th>
                                 <th className="py-2 px-4 text-right font-medium text-gray-600">Akce</th>
                             </tr>
                         </thead>
                         <tbody className="divide-y divide-gray-200">
                             {mappings.map(mapping => (
                                 <tr key={mapping.originalName}>
                                     <td className="py-2 px-4 text-gray-700">{mapping.originalName}</td>
                                     <td className="py-2 px-4 font-medium text-gray-800">{mapping.canonicalName}</td>
                                     <td className="py-2 px-4 text-right">
                                         <button onClick={() => setMappingToDelete(mapping)} className="text-red-500 hover:text-red-700">
                                             <DeleteIcon className="w-4 h-4" />
                                         </button>
                                     </td>
                                 </tr>
                             ))}
                             {mappings.length === 0 && (
                                 <tr>
                                     <td colSpan={3} className="py-4 px-4 text-center text-gray-500">
                                         Zatím neexistují žádná pravidla pro slučování.
                                     </td>
                                 </tr>
                             )}
                         </tbody>
                     </table>
                 </div>
             </div>


            {suggestions && (
                <ProductSuggestionModal
                    isOpen={!!suggestions}
                    onClose={() => setSuggestions(null)}
                    onConfirm={handleConfirmSuggestions}
                    suggestions={suggestions}
                />
            )}

            <ConfirmModal
                isOpen={!!mappingToDelete}
                onClose={() => setMappingToDelete(null)}
                onConfirm={handleConfirmDeleteMapping}
                title="Smazat pravidlo?"
                confirmText="Smazat"
                confirmClassName="bg-red-600 text-white rounded-md hover:bg-red-700"
            >
                {mappingToDelete && <p>Opravdu chcete smazat pravidlo pro <strong>"{mappingToDelete.originalName}"</strong>? Tuto akci nelze vrátit zpět.</p>}
            </ConfirmModal>
        </div>
    );
};
